    /* tslint:disable no-invalid-this */
    import {IToData} from "@/customizer/interfaces";
    import {IEditorUtil, Imports} from "./editor.interfaces";

    const globalImports: Imports = typeof global !== 'undefined' ? global as any : window as any;



    // tslint:disable-next-line:no-require-imports
    const serializer = new XMLSerializer();
    const TLF_NS = 'http://ns.adobe.com/textLayout/2008';

    export class TLF  {
        private readonly tlfTextAttributes:string[] = [
            'color',
            'fontFamily',
            'fontWeight',
            'fontSize',
            'fontStyle',        // bold, italic, etc
            'underline',
            'linethrough',
            'overline',
            'textAlign',
            'verticalAlign',
            'lineHeight',
            'backgroundColor'
        ];

        private readonly tlfAttributesMap:{[key:string]:string} = {
            color: 'fill'
        };

        private readonly canvasAttributesMap:{[key:string]:string};

        constructor() {
           this.canvasAttributesMap = this.invert(this.tlfAttributesMap);
        }

        /**
         * Takes a TLF XML object and returns the text along with the properties for fabric.js
         *
         * @param tlf
         * @returns {{text: string, styles: {}}}
         */
        public toData(tlf:string):IToData {
            const properties:any = {};
            const xml = this.parseXML(tlf).documentElement;

            // start by copying over root properties
            // @note: maybe we can optimize TLF first, for some reason, all of the root properties from flash are all
            // garbage, because styles seem to be defined individually inside <span>'s. A pre-processor to find
            // command styles would be best.
            this.copyProperties(xml, properties);

            const paddingTop = xml.getAttribute('paddingTop');
            if (paddingTop) {
                properties.margin = parseInt(xml.getAttribute('paddingTop'), 10);
            } else {
                properties.margin = 0;
            }

            // init vars
            let text:string = '';
            let currentText:string;
            const styles:any[] = [];
            let currentStyle:any;

            // get blocks of text (in paragraphs for TLF)
            const paragraphs = xml.getElementsByTagName('p');
            let spans:NodeListOf<HTMLSpanElement>;
            let span:HTMLSpanElement;

            // from flash, each paragraph is a new line of text (\n, not a line wrap)
            for (let i = 0; i < paragraphs.length; i++) {
                // each paragraph can have one or more span's
                spans = xml.getElementsByTagName('span');
                currentText = '';
                currentStyle = {};

                // if there was already some text previously, indicate a new line
                if (text) {
                    text += '\n';
                }

                // loop over each span and take the text and style definition (per character)
                for (let j = 0; j < spans.length; j++) {
                    span = spans[j];

                    // ignore empty spans
                    if (span.childNodes.length === 0) {
                        continue;
                    }

                    if (paragraphs.length === 1 && spans.length === 1) {
                        // optimization: there is only one paragraph with one span, merge with properties
                        this.copyProperties(span, properties);
                    } else {
                        // otherwise, we need the style property for each character
                        // we go one step further by only returning styles not defined in root
                        const tmpStyles = this.getDifferentProperties(properties, this.copyProperties(span, {}));

                        // if there are differences between root and this span, we need to include the data, per character
                        if (Object.keys(tmpStyles).length !== 0) {
                            if (span && span.childNodes.length > 0 && span.childNodes[0].nodeValue) {
                                for (let k = 0; k < span.childNodes[0].nodeValue.length; k++) {
                                    currentStyle[currentText.length + k] = tmpStyles;
                                }
                            }
                        }
                    }

                    // append span's text to the current line's text
                    currentText += span.childNodes[0].nodeValue;
                }

                // append paragraph's text to final text object
                text += currentText;

                // if there were style definitions for this paragraph, add it to styles object
                if (Object.keys(currentStyle).length !== 0) {
                    styles[i] = currentStyle;
                }
            }

            return {
                text: text,
                rootStyle: properties,
                styles: styles
            };
        }

        /**
         * Takes an object {text, styles} and generates a TLF XML object.
         *
         * @note: It is currently intentionally VERY naive, generating a ton of <span>'s, but this is a very initial version
         * and optimization isn't necessary, as there may not even be a scenario in which we go from fabric.js to flash
         *
         * @param element
         * @returns {string}
         */
        public toTLF(element:IToData):string {
            const text = element.text;
            const lines = text.split('\n');
            const rootStyle = element.rootStyle;
            const styles:any = element.styles;

            // create root element
            const root = document.createElementNS(TLF_NS, 'TextFlow') as HTMLElement;

            // add flex related data to rootStyle
            rootStyle.version = '3.0.0';

            // add rootStyle as attributes to root
            this.setAttributes(root, rootStyle);

            // generate the paragraphs and spans
            for (let i = 0; i < lines.length; i++) {
                const line = lines[i];
                const style = styles[i];

                const p = document.createElementNS(TLF_NS, 'p');

                if (style) {
                    for (let j = 0; j < line.length; j++) {
                        p.appendChild(this.generateSpan(line[j], style[j]));
                    }
                } else {
                    p.appendChild(this.generateSpan(line, null));
                }

                root.appendChild(p);
            }

            return serializer.serializeToString(root);
        }

        ///////////////////////
        // PRIVATE FUNCTIONS //
        ///////////////////////
        private getDifferentProperties(a:any, b:any):{[key:string]:any} {
            const result:{[key:string]:any} = {};

            for (const key in b) {
                if (b.hasOwnProperty(key) && (!a.hasOwnProperty(key) || a[key] !== b[key])) {
                    result[key] = b[key];
                }
            }

            return result;
        }

        private copyProperties(elem:HTMLElement, obj:any):any {
            if (!elem.attributes) {
                return obj;
            }

            for (let i = 0; i < elem.attributes.length; i++) {
                const attr = elem.attributes[i];

                if (attr.specified && this.tlfTextAttributes.indexOf(attr.name) >= 0) {
                    const name = this.tlfAttributesMap.hasOwnProperty(attr.name) ? this.tlfAttributesMap[attr.name] : attr.name;
                    let value:any = attr.value;

                    if (name === 'lineHeight') {
                        value = parseInt(value as string, 10) / 100;
                    }

                    obj[name] = value;
                }
            }

            return obj;
        }

        private generateSpan(text:string, styles:any):Element {
            const span = document.createElementNS(TLF_NS, 'span') as HTMLElement;
            span.innerHTML = text;

            if (styles) {
                this.setAttributes(span, styles);
            }

            return span;
        }

        /**
         * Take an object with k => v pairs and assign it as attributes
         *
         * @param element
         * @param styles
         */
        private setAttributes(element:HTMLElement, styles:any):void {

            for (const key in styles) {
                if (key === 'styles') {
                    continue;
                }

                if (styles.hasOwnProperty(key)) {
                    // margin is a special property
                    if (key === 'margin') {
                        element.setAttribute('paddingLeft', styles[key]);
                        element.setAttribute('paddingTop', styles[key]);
                        element.setAttribute('paddingRight', styles[key]);
                        element.setAttribute('paddingBottom', styles[key]);
                    } else {
                        const name = this.canvasAttributesMap.hasOwnProperty(key) ? this.canvasAttributesMap[key] : key;
                        element.setAttribute(name, styles[key]);
                    }
                }
            }
        }

        /**
         * From underscore
         *
         * @param obj
         * @returns {{}}
         */
        private invert(obj:{[key:string]:string}):{[key:string]:string} {
            const result:{[key:string]:string} = {};
            const keys = Object.keys(obj);

            for (let i = 0, length = keys.length; i < length; i++) {
                result[obj[keys[i]]] = keys[i];
            }

            return result;
        }

        /**
         * From jQuery
         *
         * @param data
         * @returns {*}
         */
        private parseXML(data:string):any {
            let xml:any;
            let tmp:DOMParser;

            try {
                tmp = new DOMParser();
                xml = tmp.parseFromString(data, 'text/xml');
            } catch (e) {
                xml = undefined;
            }

            if (!xml || !xml.documentElement || xml.getElementsByTagName('parsererror').length) {
                throw new Error('Invalid XML: ' + data);
            }

            return xml;
        }
    }

    const SF = globalImports.SF || (globalImports.SF = {});
    SF.Util = SF.Util || {} as IEditorUtil;
    SF.Util.TLF = new TLF();

